import { Minus, Plus, Users } from 'lucide-react';
import { memo } from 'react';

import { useT } from '@/app/i18n/client';
import type { IAccommodationPriceOption, ISelectedDates } from '@/entities/accommodation/types';
import { useCurrency } from '@/features/product/contexts/CurrencyContext';
import { utilDateFormat } from '@/lib/date';
import { cn, utilFromMinorUnit } from '@/lib/utils';

interface IProps {
  selectedDates: ISelectedDates;
  selectedPeriodAddPeoplePrice: number;
  selectedPeriodRoomPriceList: IAccommodationPriceOption[];
  calculatedAddPeoplePrice: number;
  standardPeople?: number;
  addPeople?: number;
  addPeopleQuantity: number;
  updateQuantity: (change: number) => (e: React.MouseEvent<HTMLButtonElement>) => void;
}
function AccommodationGuestQuantity({
  selectedDates,
  selectedPeriodAddPeoplePrice: _selectedPeriodAddPeoplePrice,
  selectedPeriodRoomPriceList,
  calculatedAddPeoplePrice,
  standardPeople = 0,
  addPeople = 0,
  addPeopleQuantity,
  updateQuantity,
}: IProps) {
  const { t } = useT('product');

  const currency = useCurrency();

  const isNextDisabled = addPeopleQuantity >= addPeople;
  const isDisabled = selectedDates.checkIn === null || selectedDates.checkOut === null;

  return (
    <div className='mb-12'>
      <div className='mb-6 space-y-2'>
        <h3 className='text-xl font-bold text-[#2E2E2E] max-sm:text-lg'>{t('guestQuantity')}</h3>
        <div className='flex items-center gap-1'>
          <Users className='h-[16px] w-[16px] text-[#0064FF]' />
          <span className='text-sm text-gray-500'>
            {t('basic')} {standardPeople}P / {t('max')} {standardPeople + addPeople}P
          </span>
        </div>
      </div>

      {/* 기본 인원 */}
      <div className='flex flex-col justify-between gap-3 rounded-2xl border border-[#E9ECEF] bg-white p-4 sm:flex-row sm:items-center sm:p-5'>
        <span className='text-base font-bold text-[#2E2E2E]'>{t('guest')}</span>
        <div className='flex flex-col items-end gap-4 max-sm:gap-2 sm:flex-row sm:items-center'>
          {addPeopleQuantity > 0 && (
            <div className='hidden items-center space-x-4 sm:flex'>
              <span className='text-base font-bold text-[#2E2E2E]'>
                {currency} {utilFromMinorUnit(calculatedAddPeoplePrice, currency)}
              </span>
            </div>
          )}
          <div className='flex items-center gap-6 max-sm:w-full'>
            {/* 빼기 버튼 */}
            <button
              onClick={addPeopleQuantity > 0 ? updateQuantity(-1) : undefined}
              className='flex h-8 w-8 items-center justify-center rounded-xl bg-[#E9ECEF] transition-all duration-200 hover:bg-[#E9ECEF] disabled:opacity-50 sm:h-10 sm:w-10'
              disabled={addPeopleQuantity <= 0}
            >
              <Minus className='h-5 w-5 text-[#2E2E2E]' />
            </button>

            <span className='mx-auto w-16 text-center text-lg font-bold text-[#2E2E2E]'>
              {standardPeople + addPeopleQuantity}
            </span>

            {/* 더하기 버튼 */}
            <button
              onClick={!isNextDisabled && !isDisabled ? updateQuantity(1) : undefined}
              className={cn(
                'flex h-8 w-8 items-center justify-center rounded-xl bg-[#0064FF] text-white transition-all duration-200 hover:bg-[#0052CC] disabled:opacity-50 sm:h-10 sm:w-10',
                isNextDisabled && 'bg-[#E9ECEF] hover:bg-[#E9ECEF]',
              )}
              disabled={isNextDisabled || isDisabled}
            >
              <Plus className={cn('h-5 w-5', isNextDisabled && 'text-[#2E2E2E]')} />
            </button>
          </div>
        </div>
      </div>

      {/* 날짜별 가격 리스트 */}
      {selectedPeriodRoomPriceList.length > 0 && addPeopleQuantity > 0 && (
        <div className='mt-4 rounded-2xl border border-[#E9ECEF] bg-white p-4 sm:p-5'>
          <h4 className='mb-3 text-sm font-semibold text-[#2E2E2E]'>
            {t('extraGuestPriceByDate')}
          </h4>
          <div className='space-y-2'>
            {selectedPeriodRoomPriceList.map(priceItem => {
              const dateTotalPrice = priceItem.addPeoplePrice * addPeopleQuantity;
              return (
                <div
                  key={priceItem.idx}
                  className='flex flex-col gap-1 border-b border-[#E9ECEF] pb-2 last:border-0 last:pb-0'
                >
                  <div className='flex items-center justify-between'>
                    <span className='text-sm text-gray-600'>
                      {utilDateFormat(priceItem.date, 'MM.DD (dd)')}
                    </span>
                    <div className='flex flex-col items-end gap-0.5'>
                      {/* <span className='text-xs text-gray-500'>
                        {currency} {utilFromMinorUnit(priceItem.addPeoplePrice, currency)}
                      </span> */}
                      <span className='text-right text-sm font-semibold text-[#2E2E2E]'>
                        {addPeopleQuantity} ×{' '}
                        {utilFromMinorUnit(priceItem.addPeoplePrice, currency)} = {currency}{' '}
                        {utilFromMinorUnit(dateTotalPrice, currency)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default memo(AccommodationGuestQuantity);
